// src/components/ProtectedRoute.tsx - COMPLETELY FIXED
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requireAspirant?: boolean;
  requireCitizen?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles,
  requireAspirant = false,
  requireCitizen = false
}) => {
  const { user, aspirant, citizen, isLoading } = useAuth();
  const location = useLocation();

  // Remove the refreshAuth from useEffect - it's causing infinite loops
  // The AuthContext already initializes auth on mount

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('🔍 ProtectedRoute Check:', {
    path: location.pathname,
    hasUser: !!user,
    userRole: user?.role,
    allowedRoles,
    hasAspirant: !!aspirant,
    hasCitizen: !!citizen,
    requireAspirant,
    requireCitizen
  });

  // No user - redirect to login
  if (!user) {
    console.log('🔒 No user found, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role)) {
      console.log(`🚫 Access denied: User role ${user.role} not in`, allowedRoles);
      
      // Redirect to appropriate dashboard based on role
      switch(user.role) {
        case 'ADMIN':
        case 'STAFF':
          return <Navigate to="/admin/dashboard" replace />;
        case 'ASPIRANT':
          return <Navigate to="/aspirant/dashboard" replace />;
        case 'CITIZEN':
          return <Navigate to="/citizen/dashboard" replace />;
        default:
          return <Navigate to="/login" replace />;
      }
    }
  }

  // Check if aspirant profile is required
  if (requireAspirant && !aspirant) {
    console.log('📝 Aspirant profile required but not found');
    return <Navigate to="/aspirant/complete-profile" replace />;
  }

  // Check if citizen profile is required
  if (requireCitizen && !citizen) {
    console.log('👤 Citizen profile required but not found');
    return <Navigate to="/citizen/complete-profile" replace />;
  }

  console.log('✅ Access granted to:', user.role);
  return <>{children}</>;
};

// Also export a default for convenience
export default ProtectedRoute;